import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAccounts } from "@/hooks/useAccounts";
import { useCurriculumProgress } from "@/hooks/useCurriculumProgress";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowRight, ArrowLeft, Wallet, PiggyBank, ShieldCheck,
  CheckCircle2, Sparkles, Lock, CreditCard, TrendingUp
} from "lucide-react";
import LunimarModuleShell from "@/components/lunimar/LunimarModuleShell";

type Step = "intro" | "checking" | "savings" | "emergency" | "deposit" | "transfer_savings" | "transfer_emergency" | "summary";

const STEPS: Step[] = ["intro", "checking", "savings", "emergency", "deposit", "transfer_savings", "transfer_emergency", "summary"];

const AccountSetupModule = () => {
  const navigate = useNavigate();
  const { transfer, deposit, refetch, accounts } = useAccounts();
  const { accountSetupUnlocked, accountSetupCompleted, completeAccountSetup } = useCurriculumProgress();
  const [step, setStep] = useState<Step>("intro");
  const [deposited, setDeposited] = useState(false);
  const [transferredSavings, setTransferredSavings] = useState(false);
  const [transferredEmergency, setTransferredEmergency] = useState(false);
  const [completing, setCompleting] = useState(false);

  if (!accountSetupUnlocked && !accountSetupCompleted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-muted/60 flex items-center justify-center mx-auto border border-border/50">
            <Lock className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground">Account Setup Module Locked</h1>
          <p className="text-sm text-muted-foreground">Complete the first 5 lessons in Financial Foundations to unlock this module.</p>
          <Button variant="outline" onClick={() => navigate("/dashboard")}>Back to Dashboard</Button>
        </div>
      </div>
    );
  }

  if (accountSetupCompleted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="max-w-md w-full text-center space-y-6">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
            <CheckCircle2 className="w-8 h-8 text-primary" />
          </div>
          <h1 className="font-heading text-xl font-bold text-foreground">Account Setup Complete</h1>
          <p className="text-sm text-muted-foreground">Your Checking, Savings, and Emergency Fund are active. Keep learning to unlock more accounts!</p>
          <Button onClick={() => navigate("/finances")} className="gap-2">View Your Accounts <ArrowRight className="w-4 h-4" /></Button>
        </div>
      </div>
    );
  }

  const stepIdx = STEPS.indexOf(step);

  const handleDeposit = async () => {
    try {
      await deposit(100, "Account Setup — Welcome Deposit", "completion_bonus");
      setDeposited(true);
      await refetch();
    } catch { /* non-critical: failure is safe to ignore here */ }
  };

  const handleTransferSavings = async () => {
    try {
      await transfer("checking", "savings", 25);
      setTransferredSavings(true);
      await refetch();
    } catch { /* non-critical: failure is safe to ignore here */ }
  };

  const handleTransferEmergency = async () => {
    try {
      await transfer("checking", "emergency_fund", 25);
      setTransferredEmergency(true);
      await refetch();
    } catch { /* non-critical: failure is safe to ignore here */ }
  };

  const handleComplete = async () => {
    setCompleting(true);
    await completeAccountSetup();
    await refetch();
    setCompleting(false);
    setStep("summary");
  };

  const checking = accounts.find(a => a.account_type === "checking");
  const savings = accounts.find(a => a.account_type === "savings");
  const emergency = accounts.find(a => a.account_type === "emergency_fund");

  return (
    <LunimarModuleShell
      realmKey="account_setup"
      title="Account Setup"
      stepIndex={stepIdx}
      totalSteps={STEPS.length}
      isLocked={false}
      lockMessage=""
      isCompleted={false}
      stepKey={step}
    >
      <div>
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {step === "intro" && (
              <StepCard
                icon={<Sparkles className="w-8 h-8 text-primary" />}
                title="Welcome to Account Setup"
                body="Before building wealth, you need to know where your money lives. In this module, you'll set up three foundational accounts that form the base of your financial life."
                subtext="Checking is for everyday money. Savings is for money you set aside. Emergency Fund is for unexpected costs that can throw you off track."
                action={<Button onClick={() => setStep("checking")} className="w-full gap-2">Let's Begin <ArrowRight className="w-4 h-4" /></Button>}
              />
            )}

            {step === "checking" && (
              <StepCard
                icon={<Wallet className="w-8 h-8 text-primary" />}
                title="Checking Account"
                body="Your Checking Account is where money enters your financial life. When you earn rewards from lessons, complete modules, or receive bonuses — it all flows into Checking first."
                subtext="Think of Checking as your money's front door. It's active, liquid, and ready to use at any moment."
                highlights={["All earned money deposits here first", "Used for everyday transactions", "Transfer out to other accounts"]}
                action={<Button onClick={() => setStep("savings")} className="w-full gap-2">Next: Savings <ArrowRight className="w-4 h-4" /></Button>}
              />
            )}

            {step === "savings" && (
              <StepCard
                icon={<PiggyBank className="w-8 h-8 text-accent" />}
                title="Savings Account"
                body="Your Savings Account is where you set aside money for short-term goals. Unlike Checking, this money is meant to grow — not be spent immediately."
                subtext="Whether it's saving for a vehicle, a course, or building a buffer — Savings is your disciplined reserve."
                highlights={["For short-term goals", "Transferred from Checking", "Builds financial discipline"]}
                action={<Button onClick={() => setStep("emergency")} className="w-full gap-2">Next: Emergency Fund <ArrowRight className="w-4 h-4" /></Button>}
              />
            )}

            {step === "emergency" && (
              <StepCard
                icon={<ShieldCheck className="w-8 h-8 text-accent" />}
                title="Emergency Fund"
                body="Your Emergency Fund protects you from surprise expenses — medical bills, car repairs, job loss, or anything unexpected that could derail your finances."
                subtext="Without an emergency fund, one surprise expense can wipe out all your progress. This account is your safety net."
                highlights={["For unexpected costs only", "Prevents financial setbacks", "Aim for 3-6 months of expenses"]}
                action={<Button onClick={() => { handleComplete().then(() => setStep("deposit")); }} disabled={completing} className="w-full gap-2">
                  {completing ? "Unlocking Accounts..." : "Unlock My Accounts"} <ArrowRight className="w-4 h-4" />
                </Button>}
              />
            )}

            {step === "deposit" && (
              <StepCard
                icon={<Wallet className="w-8 h-8 text-primary" />}
                title="Your First Deposit"
                body="Your accounts are now active! Let's make your first deposit. We'll add $100 to your Checking Account as a welcome bonus."
                subtext={checking ? `Current Checking Balance: $${checking.balance}` : ""}
                action={
                  deposited ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-primary"><CheckCircle2 className="w-4 h-4" /> $100 deposited to Checking!</div>
                      <Button onClick={() => setStep("transfer_savings")} className="w-full gap-2">Next: Transfer to Savings <ArrowRight className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <Button onClick={handleDeposit} className="w-full gap-2">Deposit $100 to Checking <ArrowRight className="w-4 h-4" /></Button>
                  )
                }
              />
            )}

            {step === "transfer_savings" && (
              <StepCard
                icon={<PiggyBank className="w-8 h-8 text-accent" />}
                title="Transfer to Savings"
                body="Now let's move $25 from Checking to Savings. This is how you separate money by purpose — keeping some accessible and some set aside."
                subtext={`Checking: $${checking?.balance || 0} · Savings: $${savings?.balance || 0}`}
                action={
                  transferredSavings ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-primary"><CheckCircle2 className="w-4 h-4" /> $25 transferred to Savings!</div>
                      <Button onClick={() => setStep("transfer_emergency")} className="w-full gap-2">Next: Emergency Fund <ArrowRight className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <Button onClick={handleTransferSavings} className="w-full gap-2">Transfer $25 to Savings <ArrowRight className="w-4 h-4" /></Button>
                  )
                }
              />
            )}

            {step === "transfer_emergency" && (
              <StepCard
                icon={<ShieldCheck className="w-8 h-8 text-accent" />}
                title="Fund Your Emergency Reserve"
                body="Finally, let's move $25 from Checking to your Emergency Fund. Even small contributions build protection over time."
                subtext={`Checking: $${checking?.balance || 0} · Emergency: $${emergency?.balance || 0}`}
                action={
                  transferredEmergency ? (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 text-sm text-primary"><CheckCircle2 className="w-4 h-4" /> $25 transferred to Emergency Fund!</div>
                      <Button onClick={() => setStep("summary")} className="w-full gap-2">View Summary <ArrowRight className="w-4 h-4" /></Button>
                    </div>
                  ) : (
                    <Button onClick={handleTransferEmergency} className="w-full gap-2">Transfer $25 to Emergency Fund <ArrowRight className="w-4 h-4" /></Button>
                  )
                }
              />
            )}

            {step === "summary" && (
              <div className="space-y-6 text-center">
                <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto border border-primary/20">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <h2 className="font-heading text-xl font-bold text-foreground">Financial Base Established</h2>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  Your three foundational accounts are active and funded. All future lesson earnings will flow into Checking automatically.
                </p>

                <div className="grid grid-cols-3 gap-3">
                  <AccountSummaryCard icon={<Wallet className="w-5 h-5" />} label="Checking" balance={checking?.balance || 0} />
                  <AccountSummaryCard icon={<PiggyBank className="w-5 h-5" />} label="Savings" balance={savings?.balance || 0} />
                  <AccountSummaryCard icon={<ShieldCheck className="w-5 h-5" />} label="Emergency" balance={emergency?.balance || 0} />
                </div>

                <div className="rounded-2xl border border-border bg-card/50 p-4 text-left space-y-2">
                  <p className="text-xs font-heading font-semibold text-muted-foreground uppercase tracking-wider">Coming Later</p>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Lock className="w-4 h-4 shrink-0" /><span>Credit Account — unlocks after Credit & Debt Management</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <Lock className="w-4 h-4 shrink-0" /><span>Investment, Roth IRA, 401(k) — unlock after Retirement & Investment Accounts</span>
                  </div>
                </div>

                <div className="flex flex-col gap-3 pt-2">
                  <Button onClick={() => navigate("/finances")} className="w-full gap-2">View Financial Dashboard <ArrowRight className="w-4 h-4" /></Button>
                  <Button variant="outline" onClick={() => navigate("/dashboard")} className="w-full">Back to Dashboard</Button>
                </div>
              </div>
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </LunimarModuleShell>
  );
};

function StepCard({ icon, title, body, subtext, highlights, action }: {
  icon: React.ReactNode;
  title: string;
  body: string;
  subtext?: string;
  highlights?: string[];
  action: React.ReactNode;
}) {
  return (
    <div className="space-y-6">
      <div className="flex flex-col items-center text-center space-y-4">
        <div className="w-16 h-16 rounded-2xl bg-muted/60 flex items-center justify-center border border-border/50">
          {icon}
        </div>
        <h2 className="font-heading text-xl font-bold text-foreground">{title}</h2>
        <p className="text-sm text-muted-foreground leading-relaxed max-w-sm">{body}</p>
        {subtext && <p className="text-xs text-muted-foreground/70 font-mono">{subtext}</p>}
      </div>
      {highlights && (
        <div className="space-y-2">
          {highlights.map((h, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-foreground/80">
              <CheckCircle2 className="w-3.5 h-3.5 text-primary shrink-0" />
              {h}
            </div>
          ))}
        </div>
      )}
      <div>{action}</div>
    </div>
  );
}

function AccountSummaryCard({ icon, label, balance }: { icon: React.ReactNode; label: string; balance: number }) {
  return (
    <div className="rounded-2xl border border-border bg-card/50 p-3 text-center space-y-1">
      <div className="text-primary mx-auto">{icon}</div>
      <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{label}</p>
      <p className="font-heading font-bold text-sm text-foreground">${balance}</p>
    </div>
  );
}

export default AccountSetupModule;
